import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Bell, Download, Brain, TrendingUp, TrendingDown, DollarSign,
  Building2, Target, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp,
  ArrowDown, BarChart3, PieChart, Eye, Users, Briefcase, Activity,
  Percent, Globe, Shield, Layers
} from 'lucide-react';
import './InstitutionalHoldingsPage.css';

// =============================================================================
// MOCK DATA — structured to match FMP /v3/institutional-holder endpoint
// Fields: holder, shares, dateReported, change, changePercent, value, weight
// =============================================================================

const mockInstitutionalFilings = [
  { id: 1, institution: 'Vanguard Group Inc', cik: '0000102909', type: 'Investment Advisor', aum: 8600000000000, holdings: 4521, topHolding: 'AAPL', topWeight: 7.2, filingDate: '2026-02-05', quarterEnd: 'Q4 2025', totalValue: 4280000000000, changeInValue: 125000000000, changePercent: 3.01, newPositions: 142, closedPositions: 87 },
  { id: 2, institution: 'BlackRock Inc', cik: '0001364742', type: 'Investment Advisor', aum: 10500000000000, holdings: 3892, topHolding: 'AAPL', topWeight: 6.8, filingDate: '2026-02-04', quarterEnd: 'Q4 2025', totalValue: 3950000000000, changeInValue: 98000000000, changePercent: 2.54, newPositions: 215, closedPositions: 134 },
  { id: 3, institution: 'State Street Corp', cik: '0000093751', type: 'Investment Advisor', aum: 4100000000000, holdings: 2845, topHolding: 'AAPL', topWeight: 6.5, filingDate: '2026-02-03', quarterEnd: 'Q4 2025', totalValue: 2180000000000, changeInValue: 45000000000, changePercent: 2.11, newPositions: 78, closedPositions: 52 },
  { id: 4, institution: 'Berkshire Hathaway Inc', cik: '0001067983', type: 'Holding Company', aum: 980000000000, holdings: 42, topHolding: 'AAPL', topWeight: 28.9, filingDate: '2026-02-03', quarterEnd: 'Q4 2025', totalValue: 378000000000, changeInValue: -12000000000, changePercent: -3.08, newPositions: 3, closedPositions: 2 },
  { id: 5, institution: 'FMR LLC (Fidelity)', cik: '0000315066', type: 'Investment Advisor', aum: 4900000000000, holdings: 5234, topHolding: 'MSFT', topWeight: 5.4, filingDate: '2026-02-02', quarterEnd: 'Q4 2025', totalValue: 2850000000000, changeInValue: 67000000000, changePercent: 2.41, newPositions: 312, closedPositions: 198 },
  { id: 6, institution: 'JPMorgan Chase & Co', cik: '0000019617', type: 'Bank/Investment Advisor', aum: 3400000000000, holdings: 4156, topHolding: 'AAPL', topWeight: 4.2, filingDate: '2026-02-01', quarterEnd: 'Q4 2025', totalValue: 1920000000000, changeInValue: 34000000000, changePercent: 1.80, newPositions: 189, closedPositions: 145 },
  { id: 7, institution: 'Citadel Advisors LLC', cik: '0001423053', type: 'Hedge Fund', aum: 620000000000, holdings: 8921, topHolding: 'NVDA', topWeight: 3.1, filingDate: '2026-01-31', quarterEnd: 'Q4 2025', totalValue: 456000000000, changeInValue: 52000000000, changePercent: 12.87, newPositions: 1245, closedPositions: 987 },
  { id: 8, institution: 'Bridgewater Associates', cik: '0001350694', type: 'Hedge Fund', aum: 168000000000, holdings: 892, topHolding: 'SPY', topWeight: 8.4, filingDate: '2026-01-30', quarterEnd: 'Q4 2025', totalValue: 24500000000, changeInValue: -3200000000, changePercent: -11.55, newPositions: 145, closedPositions: 212 },
  { id: 9, institution: 'Renaissance Technologies', cik: '0001037389', type: 'Hedge Fund', aum: 130000000000, holdings: 3456, topHolding: 'NVDA', topWeight: 2.8, filingDate: '2026-01-29', quarterEnd: 'Q4 2025', totalValue: 98000000000, changeInValue: 8500000000, changePercent: 9.50, newPositions: 678, closedPositions: 542 },
  { id: 10, institution: 'Geode Capital Management', cik: '0001214717', type: 'Investment Advisor', aum: 1100000000000, holdings: 3245, topHolding: 'AAPL', topWeight: 6.9, filingDate: '2026-01-28', quarterEnd: 'Q4 2025', totalValue: 842000000000, changeInValue: 21000000000, changePercent: 2.56, newPositions: 95, closedPositions: 67 },
  { id: 11, institution: 'Morgan Stanley', cik: '0000895421', type: 'Bank/Investment Advisor', aum: 2800000000000, holdings: 3678, topHolding: 'MSFT', topWeight: 3.9, filingDate: '2026-01-27', quarterEnd: 'Q4 2025', totalValue: 1540000000000, changeInValue: 28000000000, changePercent: 1.85, newPositions: 167, closedPositions: 132 },
  { id: 12, institution: 'Goldman Sachs Group', cik: '0000886982', type: 'Bank/Investment Advisor', aum: 2500000000000, holdings: 4012, topHolding: 'AAPL', topWeight: 3.5, filingDate: '2026-01-26', quarterEnd: 'Q4 2025', totalValue: 1380000000000, changeInValue: 15000000000, changePercent: 1.10, newPositions: 201, closedPositions: 178 },
  { id: 13, institution: 'T. Rowe Price Associates', cik: '0001003078', type: 'Investment Advisor', aum: 1500000000000, holdings: 2134, topHolding: 'AMZN', topWeight: 5.1, filingDate: '2026-01-25', quarterEnd: 'Q4 2025', totalValue: 780000000000, changeInValue: -8900000000, changePercent: -1.13, newPositions: 56, closedPositions: 89 },
  { id: 14, institution: 'Two Sigma Investments', cik: '0001179392', type: 'Hedge Fund', aum: 67000000000, holdings: 6234, topHolding: 'SPY', topWeight: 4.2, filingDate: '2026-01-24', quarterEnd: 'Q4 2025', totalValue: 38000000000, changeInValue: 4200000000, changePercent: 12.43, newPositions: 1567, closedPositions: 1234 },
  { id: 15, institution: 'Capital Research & Mgmt', cik: '0000044866', type: 'Investment Advisor', aum: 2700000000000, holdings: 1245, topHolding: 'META', topWeight: 6.3, filingDate: '2026-01-23', quarterEnd: 'Q4 2025', totalValue: 1650000000000, changeInValue: 42000000000, changePercent: 2.61, newPositions: 34, closedPositions: 21 },
  { id: 16, institution: 'Wellington Management', cik: '0000105897', type: 'Investment Advisor', aum: 1200000000000, holdings: 2567, topHolding: 'MSFT', topWeight: 4.7, filingDate: '2026-01-22', quarterEnd: 'Q4 2025', totalValue: 720000000000, changeInValue: 9800000000, changePercent: 1.38, newPositions: 89, closedPositions: 72 },
  { id: 17, institution: 'Millennium Management', cik: '0001273087', type: 'Hedge Fund', aum: 62000000000, holdings: 7845, topHolding: 'NVDA', topWeight: 2.4, filingDate: '2026-01-21', quarterEnd: 'Q4 2025', totalValue: 42000000000, changeInValue: 6700000000, changePercent: 18.97, newPositions: 2134, closedPositions: 1876 },
  { id: 18, institution: 'Point72 Asset Management', cik: '0001603466', type: 'Hedge Fund', aum: 34000000000, holdings: 1892, topHolding: 'AMZN', topWeight: 3.8, filingDate: '2026-01-20', quarterEnd: 'Q4 2025', totalValue: 28500000000, changeInValue: 3400000000, changePercent: 13.55, newPositions: 456, closedPositions: 389 },
  { id: 19, institution: 'Northern Trust Corp', cik: '0000073124', type: 'Investment Advisor', aum: 1400000000000, holdings: 2890, topHolding: 'AAPL', topWeight: 6.1, filingDate: '2026-01-19', quarterEnd: 'Q4 2025', totalValue: 890000000000, changeInValue: 12000000000, changePercent: 1.37, newPositions: 67, closedPositions: 45 },
  { id: 20, institution: 'Invesco Ltd', cik: '0000914208', type: 'Investment Advisor', aum: 1600000000000, holdings: 3123, topHolding: 'QQQ', topWeight: 5.8, filingDate: '2026-01-18', quarterEnd: 'Q4 2025', totalValue: 670000000000, changeInValue: -5600000000, changePercent: -0.83, newPositions: 112, closedPositions: 134 },
];

// Big moves / notable position changes
const mockBigMoves = [
  { institution: 'Citadel Advisors', ticker: 'NVDA', company: 'NVIDIA Corp', action: 'New Position', shares: 3200000, value: 456000000, date: '2026-02-05' },
  { institution: 'Berkshire Hathaway', ticker: 'OXY', company: 'Occidental Petroleum', action: 'Added', shares: 8400000, value: 504000000, date: '2026-02-03' },
  { institution: 'Renaissance Tech', ticker: 'TSLA', company: 'Tesla Inc', action: 'Closed', shares: -2100000, value: -828900000, date: '2026-01-29' },
  { institution: 'Bridgewater', ticker: 'GLD', company: 'SPDR Gold Trust', action: 'Added', shares: 1500000, value: 312000000, date: '2026-01-30' },
  { institution: 'Point72', ticker: 'PLTR', company: 'Palantir', action: 'New Position', shares: 4500000, value: 371700000, date: '2026-01-20' },
];

// Sector allocation across top institutions
const mockSectorAllocation = [
  { sector: 'Technology', weight: 31.2, change: 1.8 },
  { sector: 'Healthcare', weight: 14.5, change: -0.6 },
  { sector: 'Financials', weight: 13.8, change: 0.4 },
  { sector: 'Consumer Disc.', weight: 10.2, change: 0.9 },
  { sector: 'Communication', weight: 8.7, change: 1.2 },
  { sector: 'Industrials', weight: 7.3, change: -0.3 },
  { sector: 'Energy', weight: 5.1, change: -1.4 },
  { sector: 'Consumer Staples', weight: 4.2, change: -0.2 },
  { sector: 'Utilities', weight: 2.8, change: 0.3 },
  { sector: 'Real Estate', weight: 2.2, change: -0.1 },
];

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

const formatCurrency = (num) => {
  if (!num && num !== 0) return '—';
  if (Math.abs(num) >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (Math.abs(num) >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (Math.abs(num) >= 1e6) return `$${(num / 1e6).toFixed(1)}M`;
  if (Math.abs(num) >= 1e3) return `$${(num / 1e3).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
};

const formatNumber = (num) => {
  if (!num && num !== 0) return '—';
  return num.toLocaleString();
};

function AIInsightCard({ insight }) {
  return (
    <div className="ih-ai-insight">
      <div className="ih-ai-header">
        <div className="ih-ai-badge"><Brain size={14} /> AI Insight</div>
        <span className="ih-ai-updated">Updated 3 hours ago</span>
      </div>
      <p className="ih-ai-text" dangerouslySetInnerHTML={{ __html: insight }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, subType = 'neutral', color }) {
  return (
    <div className="ih-stat-card" style={{ background: color }}>
      <div className="ih-stat-icon" style={{ background: color }}>
        <Icon size={20} />
      </div>
      <div className="ih-stat-label">{label}</div>
      <div className="ih-stat-value">{value}</div>
      <div className={`ih-stat-sub ${subType}`}>{sub}</div>
    </div>
  );
}

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================
export default function InstitutionalHoldingsPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortBy, setSortBy] = useState('filingDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  // Type filters
  const typeFilters = ['All', 'Advisors', 'Hedge Funds', 'Banks'];

  const getTypeMatch = (type, filter) => {
    if (filter === 'All') return true;
    if (filter === 'Advisors') return type === 'Investment Advisor';
    if (filter === 'Hedge Funds') return type === 'Hedge Fund';
    if (filter === 'Banks') return type.includes('Bank');
    return true;
  };

  // Filter + sort
  const filteredFilings = useMemo(() => {
    let data = [...mockInstitutionalFilings];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      data = data.filter(f => f.institution.toLowerCase().includes(q) || f.topHolding.toLowerCase().includes(q));
    }
    if (typeFilter !== 'All') {
      data = data.filter(f => getTypeMatch(f.type, typeFilter));
    }
    data.sort((a, b) => {
      let aVal = a[sortBy], bVal = b[sortBy];
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [searchTerm, typeFilter, sortBy, sortOrder]);

  const totalPages = Math.ceil(filteredFilings.length / perPage);
  const pagedFilings = filteredFilings.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={12} style={{ opacity: 0.3 }} />;
    return sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  // Stats
  const stats = useMemo(() => {
    const totalAUM = mockInstitutionalFilings.reduce((s, f) => s + f.aum, 0);
    const totalFilings = mockInstitutionalFilings.length;
    const hedgeFunds = mockInstitutionalFilings.filter(f => f.type === 'Hedge Fund').length;
    const avgChange = (mockInstitutionalFilings.reduce((s, f) => s + f.changePercent, 0) / totalFilings).toFixed(2);
    return { totalAUM, totalFilings, hedgeFunds, avgChange };
  }, []);

  const aiInsight = `<span class="highlight">Net institutional flow is +$12.4B this month.</span> Tech leads at +$2.8B, followed by Financials at +$1.2B. Notable: <span class="highlight">Citadel</span> opened a new $456M NVDA position, <span class="highlight">Berkshire</span> added 8.4M OXY shares, and <span class="highlight">Renaissance Technologies</span> fully exited TSLA. Hedge fund turnover remains elevated at 18-19% QoQ. ARK continues accumulating PLTR despite price weakness.`;

  return (
    <div className="institutional-holdings-page">
      {/* Header */}
      <div className="ih-page-header">
        <div className="ih-header-content">
          <div className="ih-header-title">
            <div className="ih-header-icon"><Building2 size={22} color="#fff" /></div>
            <div>
              <h1>Institutional Holdings</h1>
              <p>13F filings • Hedge funds, advisors & institutional investors</p>
            </div>
          </div>
          <div className="ih-header-actions">
            <button className="ih-header-btn"><Download size={14} /> Export</button>
            <button className="ih-header-btn primary"><Bell size={14} /> Set Alerts</button>
          </div>
        </div>
      </div>

      {/* AI Insight */}
      <AIInsightCard insight={aiInsight} />

      {/* Stats Row */}
      <div className="ih-stats-row">
        <StatCard icon={Building2} label="Total AUM Tracked" value={formatCurrency(stats.totalAUM)} sub={`${stats.totalFilings} institutions`} subType="neutral" color="linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))" />
        <StatCard icon={TrendingUp} label="Avg QoQ Change" value={`${stats.avgChange > 0 ? '+' : ''}${stats.avgChange}%`} sub="Portfolio value change" subType={stats.avgChange >= 0 ? 'positive' : 'negative'} color="linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))" />
        <StatCard icon={Shield} label="Hedge Funds Tracked" value={stats.hedgeFunds.toString()} sub="Active 13F filers" subType="neutral" color="linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))" />
        <StatCard icon={Activity} label="Net Institutional Flow" value="+$12.4B" sub="Monthly net buying" subType="positive" color="linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))" />
      </div>

      {/* Content Grid */}
      <div className="ih-content-grid">
        {/* Main Table Panel */}
        <div className="ih-table-panel">
          <div className="ih-panel-header">
            <h2><Building2 size={16} /> Recent 13F Filings <span className="ih-record-count">{filteredFilings.length}</span></h2>
          </div>

          {/* Toolbar */}
          <div className="ih-toolbar">
            <div className="ih-search-box">
              <Search size={14} />
              <input type="text" placeholder="Search institution or holding..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
            </div>
            <div className="ih-filter-toggles">
              {typeFilters.map(f => (
                <button key={f} className={`ih-filter-btn ${typeFilter === f ? 'active' : ''}`} onClick={() => { setTypeFilter(f); setCurrentPage(1); }}>{f}</button>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="ih-table-wrap">
            <table className="ih-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('institution')}>Institution <SortIcon field="institution" /></th>
                  <th>Type</th>
                  <th onClick={() => handleSort('totalValue')}>Portfolio Value <SortIcon field="totalValue" /></th>
                  <th onClick={() => handleSort('holdings')}>Holdings <SortIcon field="holdings" /></th>
                  <th onClick={() => handleSort('changePercent')}>QoQ Change <SortIcon field="changePercent" /></th>
                  <th onClick={() => handleSort('filingDate')}>Filed <SortIcon field="filingDate" /></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedFilings.map(filing => (
                  <tr key={filing.id} className="ih-row" onClick={() => navigate(`/institution/${encodeURIComponent(filing.institution)}`)}>
                    <td>
                      <div className="ih-inst-name">{filing.institution}</div>
                      <div className="ih-inst-meta">Top: {filing.topHolding} ({filing.topWeight}%)</div>
                    </td>
                    <td><span className={`ih-type-badge ${filing.type.includes('Hedge') ? 'hedge' : filing.type.includes('Bank') ? 'bank' : 'advisor'}`}>{filing.type.includes('Hedge') ? 'Hedge Fund' : filing.type.includes('Bank') ? 'Bank' : 'Advisor'}</span></td>
                    <td>{formatCurrency(filing.totalValue)}</td>
                    <td>{formatNumber(filing.holdings)}</td>
                    <td>
                      <span className={`ih-change ${filing.changePercent >= 0 ? 'positive' : 'negative'}`}>
                        {filing.changePercent >= 0 ? '+' : ''}{filing.changePercent.toFixed(2)}%
                      </span>
                    </td>
                    <td>{new Date(filing.filingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td>
                      <button className="ih-view-btn" onClick={(e) => { e.stopPropagation(); navigate(`/stock/${filing.topHolding}?tab=institutions`); }} title={`View ${filing.topHolding} institutional data`}>
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ih-pagination">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
              <span>Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="ih-sidebar">
          {/* Sector Allocation */}
          <div className="ih-sidebar-card">
            <h3><PieChart size={16} /> Sector Allocation</h3>
            <div className="ih-sector-list">
              {mockSectorAllocation.map((s, idx) => (
                <div key={idx} className="ih-sector-row">
                  <div className="ih-sector-info">
                    <span className="ih-sector-name">{s.sector}</span>
                    <span className={`ih-sector-change ${s.change >= 0 ? 'positive' : 'negative'}`}>{s.change >= 0 ? '+' : ''}{s.change}%</span>
                  </div>
                  <div className="ih-sector-bar-bg">
                    <div className="ih-sector-bar" style={{ width: `${s.weight}%` }}></div>
                  </div>
                  <span className="ih-sector-weight">{s.weight}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Notable Moves */}
          <div className="ih-sidebar-card">
            <h3><Briefcase size={16} /> Notable Moves</h3>
            <div className="ih-moves-list">
              {mockBigMoves.map((move, idx) => (
                <div key={idx} className="ih-move-item" onClick={() => navigate(`/stock/${move.ticker}?tab=institutions`)}>
                  <div className="ih-move-header">
                    <span className="ih-move-ticker">{move.ticker}</span>
                    <span className={`ih-move-action ${move.action === 'Closed' ? 'negative' : 'positive'}`}>{move.action}</span>
                  </div>
                  <div className="ih-move-institution">{move.institution}</div>
                  <div className="ih-move-detail">
                    <span>{formatNumber(Math.abs(move.shares))} shares</span>
                    <span>{formatCurrency(Math.abs(move.value))}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Held Stocks */}
          <div className="ih-sidebar-card">
            <h3><BarChart3 size={16} /> Most Held Stocks</h3>
            <div className="ih-top-held">
              {[
                { rank: 1, ticker: 'AAPL', name: 'Apple Inc.', holders: 4521, value: '$2.8T' },
                { rank: 2, ticker: 'MSFT', name: 'Microsoft Corp.', holders: 4389, value: '$2.6T' },
                { rank: 3, ticker: 'AMZN', name: 'Amazon.com', holders: 4102, value: '$1.9T' },
                { rank: 4, ticker: 'NVDA', name: 'NVIDIA Corp.', holders: 3987, value: '$1.7T' },
                { rank: 5, ticker: 'GOOGL', name: 'Alphabet Inc.', holders: 3845, value: '$1.4T' },
              ].map(stock => (
                <div key={stock.rank} className="ih-held-item" onClick={() => navigate(`/stock/${stock.ticker}?tab=institutions`)}>
                  <span className="ih-held-rank">{stock.rank}</span>
                  <div className="ih-held-info">
                    <span className="ih-held-ticker">{stock.ticker}</span>
                    <span className="ih-held-name">{stock.name}</span>
                  </div>
                  <div className="ih-held-stats">
                    <span className="ih-held-holders">{formatNumber(stock.holders)} holders</span>
                    <span className="ih-held-value">{stock.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}